//
// # ConstraintMatchRow
//
// Compact card showing a constraint match: type label + instance names.
// Displays as: firstInstances <-> secondInstances
// with a coloured left border.
//
// Click selects the matched instances on the canvas.
// Hover gives a lightweight preview highlight.
// Right-click opens a context menu with delete option.
//

import React, { useCallback, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Pencil, Play, Trash2 } from "lucide-react";
import type { schema } from "GenAlphaSchema";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";

// Format a human-readable type label
function typeLabel(constraintType: string | undefined): string {
  if (!constraintType) return "match";
  return constraintType.replace(/_/g, " ");
}

interface ConstraintMatchRowProps {
  readonly match: schema.schematic.ConstraintMatch;
}

export function ConstraintMatchRow({ match }: ConstraintMatchRowProps) {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const isSelected =
    match.id !== undefined && snapshot.selectedConstraintMatchIds.has(match.id);

  // Click: select this constraint match (shift-click to multi-select)
  // Also highlights matched instances on the canvas.
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (match.id !== undefined) {
        workspace.selectConstraintMatch(match.id, e.shiftKey);
      }

      const editor = workspace.getActiveEditor();
      if (!editor) return;

      const content = editor.content;
      if (content.contentKind !== "schematic") return;

      // Collect all instance names from this match
      const names = new Set([
        ...match.firstInstances,
        ...match.secondInstances,
      ]);

      if (!e.shiftKey) {
        // Without shift: deselect everything first
        content.deselect();
      }

      // Find and select matching instances
      for (const instance of content.instances) {
        if (names.has(instance.data.name)) {
          content.addToSelection(instance);
        }
      }
    },
    [workspace, match]
  );

  // Hover enter: highlight without changing selection
  const handleMouseEnter = useCallback(() => {
    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    if (content.contentKind !== "schematic") return;

    const names = new Set([...match.firstInstances, ...match.secondInstances]);

    for (const instance of content.instances) {
      if (names.has(instance.data.name)) {
        instance.highlight(true);
      }
    }
    editor.requestRedraw();
  }, [workspace, match]);

  // Hover leave: remove preview highlight and restore selection highlight
  const handleMouseLeave = useCallback(() => {
    const editor = workspace.getActiveEditor();
    if (!editor) return;

    const content = editor.content;
    if (content.contentKind !== "schematic") return;

    const names = new Set([...match.firstInstances, ...match.secondInstances]);

    // Remove hover highlight from these instances
    for (const instance of content.instances) {
      if (names.has(instance.data.name)) {
        instance.highlight(false);
      }
    }

    // Restore highlight on any selected entities
    for (const entity of content.selection) {
      entity.highlight(true);
    }
    editor.requestRedraw();
  }, [workspace, match]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Right-click: show context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  // Close context menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!contextMenu) return;

    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Edit constraint (match.id is always defined for existing constraints)
  const handleEdit = useCallback(() => {
    if (match.id === undefined) return;
    setContextMenu(null);
    workspace.openEditConstraintDialog(
      match.id,
      match.description ?? "",
      match.constraintType ?? "",
      [...match.firstInstances],
      [...match.secondInstances],
      match.groupId
    );
  }, [workspace, match]);

  // Delete constraint(s).
  // If this match is selected and there are multiple selected, delete all selected.
  const handleDelete = useCallback(() => {
    if (match.id === undefined) return;
    setContextMenu(null);
    if (isSelected && snapshot.selectedConstraintMatchIds.size > 1) {
      for (const id of snapshot.selectedConstraintMatchIds) {
        void workspace.removeConstraint(id);
      }
      workspace.clearConstraintSelection();
    } else {
      void workspace.removeConstraint(match.id);
    }
  }, [workspace, match.id, isSelected, snapshot.selectedConstraintMatchIds]);

  // Place constraint(s) symmetrically (only available for layouts).
  // If this match is already selected, places all selected constraints.
  // If not selected, places just this one.
  const handlePlace = useCallback(() => {
    if (match.id === undefined) return;
    setContextMenu(null);
    if (isSelected && snapshot.selectedConstraintMatchIds.size > 0) {
      // Place all selected constraints
      void workspace.placeSelectedConstraints();
    } else {
      // Select just this one and place it
      workspace.selectConstraintMatch(match.id, false);
      void workspace.placeSelectedConstraints();
    }
  }, [workspace, match.id, isSelected, snapshot.selectedConstraintMatchIds]);

  // Check if we're viewing a layout (Place option only available for layouts)
  const editor = workspace.getActiveEditor();
  const isLayout = editor?.content.contentKind === "layout";

  return (
    <>
      <div
        className={
          "constraint-match-card" + (isSelected ? " constraint-selected" : "")
        }
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={
          match.description ??
          `${match.firstInstances.join(
            ", "
          )} \u27F7 ${match.secondInstances.join(", ")}`
        }
      >
        <div className="flex items-center gap-1.5 flex-wrap">
          {match.constraintType && (
            <span className="constraint-type-label">
              {typeLabel(match.constraintType)}
            </span>
          )}
          <span className="text-[11px] text-[var(--color-ide-text)]">
            {match.firstInstances.join(", ")}
          </span>
          <span className="constraint-arrow">{"\u27F7"}</span>
          <span className="text-[11px] text-[var(--color-ide-text)]">
            {match.secondInstances.join(", ")}
          </span>
        </div>
      </div>

      {/* Context menu */}
      {contextMenu &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 py-1 rounded shadow-lg border text-xs min-w-[120px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
              backgroundColor: "var(--color-ide-surface)",
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            {/* Place option - only for layouts */}
            {isLayout && (
              <button
                className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
                onClick={handlePlace}
              >
                <Play size={12} />
                {isSelected && snapshot.selectedConstraintMatchIds.size > 1
                  ? `Place ${snapshot.selectedConstraintMatchIds.size} selected`
                  : "Place"}
              </button>
            )}
            <button
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={handleEdit}
              disabled={
                isSelected && snapshot.selectedConstraintMatchIds.size > 1
              }
            >
              <Pencil size={12} />
              Edit
            </button>
            <button
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-white/10"
              style={{ color: "var(--color-ide-error, #e53e3e)" }}
              onClick={handleDelete}
            >
              <Trash2 size={12} />
              {isSelected && snapshot.selectedConstraintMatchIds.size > 1
                ? `Delete ${snapshot.selectedConstraintMatchIds.size} selected`
                : "Delete"}
            </button>
          </div>,
          document.body
        )}
    </>
  );
}
